import React, { useState, useEffect, useRef } from 'react';
import {
  requestNotificationPermission,
  showToastNotification,
  playAlertSound,
  SOUND_PRESETS,
} from '../utils/alertUtils';

const METRICS = {
  rr_ratio:            { label: 'R/R (%)',   suffix: '%', example: 'e.g. 1000' },
  net_delta:           { label: 'Net Delta', suffix: '',  example: 'e.g. -1' },
  min_pl_death_valley: { label: 'VoD',       suffix: '',  example: 'e.g. 0' },
  total_debit:         { label: 'Cost',      suffix: '',  example: 'e.g. 500' },
};

const OPERATORS = [
  { value: '>',  label: '>' },
  { value: '>=', label: '≥' },
  { value: '<',  label: '<' },
  { value: '<=', label: '≤' },
  { value: '==', label: '=' },
];

const DEFAULT_CONDITION = { metric: 'rr_ratio', operator: '>', value: '' };

function isEmbeddedInFrame() {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

function AlertsManager({ isOpen, onClose, config, setConfig }) {
  const [stagedConditions, setStagedConditions] = useState([]);
  const [currentCondition, setCurrentCondition] = useState(DEFAULT_CONDITION);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('unknown');
  const [permissionError, setPermissionError] = useState(null);
  const [justSaved, setJustSaved] = useState(false);
  const savedTimerRef = useRef(null);
  const firstConfigRef = useRef(true);

  // Sync permission state when modal opens
  useEffect(() => {
    if (!isOpen) return;
    setPermissionError(null);
    if ('Notification' in window) {
      setPermissionStatus(Notification.permission);
    } else {
      setPermissionStatus('unsupported');
    }
    firstConfigRef.current = true; // skip the first saved-flash on open
  }, [isOpen]);

  // When a helper popup (same origin) sets localStorage, sync permission in this frame
  useEffect(() => {
    if (!isOpen) return;
    const onStorage = (e) => {
      if (e.key === 'fusion:notify-refresh' && e.newValue && 'Notification' in window) {
        setPermissionStatus(Notification.permission);
        setPermissionError(null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [isOpen]);

  // Return focus from the helper popup: re-read permission
  useEffect(() => {
    if (!isOpen) return;
    const onFocus = () => {
      if ('Notification' in window) {
        setPermissionStatus(Notification.permission);
        if (Notification.permission === 'granted') setPermissionError(null);
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [isOpen]);

  // Esc to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // "Saved ✓" flash whenever config changes while the modal is open
  useEffect(() => {
    if (!isOpen) return;
    if (firstConfigRef.current) { firstConfigRef.current = false; return; }
    setJustSaved(true);
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setJustSaved(false), 1400);
    return () => { if (savedTimerRef.current) clearTimeout(savedTimerRef.current); };
  }, [config, isOpen]);

  if (!isOpen) return null;

  const currentMetricMeta = METRICS[currentCondition.metric] || {};

  const valueIsValid = (v) => v !== '' && !isNaN(Number(v));
  const canSave =
    stagedConditions.length > 0 || valueIsValid(currentCondition.value);

  const handleAddCondition = () => {
    if (!valueIsValid(currentCondition.value)) return;
    setStagedConditions(prev => [
      ...prev,
      { ...currentCondition, value: Number(currentCondition.value) },
    ]);
    setCurrentCondition(prev => ({ ...prev, value: '' }));
  };

  const handleRemoveStagedCondition = (index) => {
    setStagedConditions(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveRule = () => {
    let conditionsToSave = [...stagedConditions];
    if (valueIsValid(currentCondition.value)) {
      conditionsToSave.push({
        ...currentCondition,
        value: Number(currentCondition.value),
      });
    }
    if (conditionsToSave.length === 0) return;

    setConfig(prev => {
      const rules = prev.rules || [];
      if (editingRuleId) {
        return {
          ...prev,
          rules: rules.map(r =>
            r.id === editingRuleId ? { ...r, conditions: conditionsToSave } : r
          ),
        };
      }
      const ruleId = Math.random().toString(36).substring(2, 9);
      return { ...prev, rules: [...rules, { id: ruleId, conditions: conditionsToSave }] };
    });

    setStagedConditions([]);
    setCurrentCondition(prev => ({ ...prev, value: '' }));
    setEditingRuleId(null);
  };

  const handleEditRule = (rule) => {
    const conds = (rule.conditions || []).map(c => ({ ...c, value: String(c.value) }));
    if (conds.length === 0) return;
    setStagedConditions(conds.slice(0, -1).map(c => ({ ...c, value: Number(c.value) })));
    setCurrentCondition(conds[conds.length - 1]);
    setEditingRuleId(rule.id);
  };

  const handleCancelEdit = () => {
    setStagedConditions([]);
    setCurrentCondition(DEFAULT_CONDITION);
    setEditingRuleId(null);
  };

  const handleRemoveRule = (id) => {
    setConfig(prev => ({ ...prev, rules: (prev.rules || []).filter(r => r.id !== id) }));
    if (editingRuleId === id) handleCancelEdit();
  };

  const handleTestAlert = () => {
    if (config.soundEnabled) playAlertSound(config.soundId);
    if (config.toastEnabled) {
      showToastNotification('Test Alert', {
        body: 'This is a test notification from Fusion Scanner.',
        icon: '/favicon.ico',
      });
    }
  };

  const handleRequestPermission = async () => {
    setPermissionError(null);
    // Embedded: prompt rarely works in-frame; open a same-origin mini window (user stays on the main site).
    if (isEmbeddedInFrame()) {
      const url = `${window.location.origin}/notify-permission.html`;
      const w = window.open(
        url,
        'fusionNotify',
        'width=480,height=400,scrollbars=yes,resizable=yes'
      );
      if (!w) {
        setPermissionError('popup-blocked');
      }
      return;
    }
    try {
      const { granted, state } = await requestNotificationPermission();
      setPermissionStatus(state);
      if (!granted && state === 'denied') {
        setPermissionError('top-denied');
      } else if (!granted && state === 'default') {
        setPermissionError('default-pending');
      }
    } catch (e) {
      setPermissionError(e?.message || 'Could not request notification permission.');
    }
  };

  const permissionLabel = ({
    granted: 'granted',
    denied: 'denied',
    default: 'not requested',
    unsupported: 'unsupported',
    unknown: 'unknown',
  })[permissionStatus] || permissionStatus;

  const rules = config.rules || [];

  return (
    <div
      className="alerts-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="alerts-modal" role="dialog" aria-modal="true" aria-labelledby="alerts-modal-title">
        <div className="alerts-modal-header">
          <h2 id="alerts-modal-title">Alerts Configuration</h2>
          <div className="alerts-header-right">
            {justSaved && <span className="saved-indicator" aria-live="polite">Saved ✓</span>}
            <button className="close-btn" onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>

        <div className="alerts-modal-body">
          <div className="alerts-layout">
            {/* LEFT: Rules */}
            <section className={`alert-rules-section ${!config.enabled ? 'is-disabled' : ''}`}>
              <div className="section-title-row">
                <h3>Alert Rules</h3>
                <span className="badge-count" title="Number of rules">{rules.length}</span>
              </div>
              <p className="rules-desc">
                Triggers when a new combination meets <b>ALL</b> conditions of <b>ANY</b> rule.
              </p>

              <div className="rules-list">
                {rules.length === 0 ? (
                  <p className="no-rules">No rules yet — build one below.</p>
                ) : (
                  rules.map((rule, idx) => {
                    const conds = rule.conditions || [rule];
                    const isEditing = editingRuleId === rule.id;
                    return (
                      <React.Fragment key={rule.id}>
                        {idx > 0 && (
                          <div className="rules-or-sep" aria-hidden="true"><span>OR</span></div>
                        )}
                        <div className={`rule-item ${isEditing ? 'is-editing' : ''}`}>
                          <div className="rule-conditions">
                            {conds.map((c, i) => (
                              <React.Fragment key={i}>
                                {i > 0 && <span className="cond-and">AND</span>}
                                <span className="rule-chip">
                                  <b>{METRICS[c.metric]?.label || c.metric}</b>
                                  <span className="rule-chip-op">{c.operator}</span>
                                  <b>{c.value}{METRICS[c.metric]?.suffix || ''}</b>
                                </span>
                              </React.Fragment>
                            ))}
                          </div>
                          <div className="rule-actions">
                            <button
                              className="icon-btn"
                              onClick={() => handleEditRule(rule)}
                              title="Edit rule"
                              aria-label="Edit rule"
                            >✎</button>
                            <button
                              className="icon-btn icon-btn-danger"
                              onClick={() => handleRemoveRule(rule.id)}
                              title="Remove rule"
                              aria-label="Remove rule"
                            >🗑</button>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })
                )}
              </div>

              <div className="rule-builder">
                <div className="rule-builder-title">
                  <h4>{editingRuleId ? 'Edit Rule' : 'Build New Rule'}</h4>
                  {editingRuleId && (
                    <button className="link-btn" onClick={handleCancelEdit}>Cancel</button>
                  )}
                </div>

                {stagedConditions.length > 0 && (
                  <div className="staged-conditions">
                    {stagedConditions.map((c, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <span className="cond-and-chip">AND</span>}
                        <span className="staged-condition-chip">
                          <span>
                            {METRICS[c.metric]?.label} {c.operator} {c.value}
                            {METRICS[c.metric]?.suffix || ''}
                          </span>
                          <button
                            onClick={() => handleRemoveStagedCondition(i)}
                            aria-label="Remove condition"
                          >×</button>
                        </span>
                      </React.Fragment>
                    ))}
                  </div>
                )}

                <div className="add-condition-form">
                  {stagedConditions.length > 0 && <span className="cond-and-label">AND</span>}
                  <select
                    value={currentCondition.metric}
                    onChange={(e) => setCurrentCondition(prev => ({ ...prev, metric: e.target.value }))}
                    aria-label="Metric"
                  >
                    {Object.entries(METRICS).map(([key, meta]) => (
                      <option key={key} value={key}>{meta.label}</option>
                    ))}
                  </select>
                  <select
                    value={currentCondition.operator}
                    onChange={(e) => setCurrentCondition(prev => ({ ...prev, operator: e.target.value }))}
                    aria-label="Operator"
                  >
                    {OPERATORS.map(op => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                  <div className="input-with-suffix">
                    <input
                      type="number"
                      placeholder={currentMetricMeta.example || 'Value'}
                      value={currentCondition.value}
                      onChange={(e) => setCurrentCondition(prev => ({ ...prev, value: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAddCondition(); }}
                      aria-label="Value"
                    />
                    {currentMetricMeta.suffix && (
                      <span className="input-suffix">{currentMetricMeta.suffix}</span>
                    )}
                  </div>
                  <button
                    className="add-cond-btn"
                    onClick={handleAddCondition}
                    title="Add another condition (AND)"
                    disabled={!valueIsValid(currentCondition.value)}
                  >
                    + AND
                  </button>
                </div>

                <div className="rule-builder-actions">
                  <button
                    className="save-rule-btn"
                    onClick={handleSaveRule}
                    disabled={!canSave}
                    title={!canSave ? 'Add a value first.' : ''}
                  >
                    {editingRuleId ? 'Update Rule' : 'Save Rule'}
                  </button>
                </div>
              </div>
            </section>

            {/* RIGHT: Settings */}
            <aside className="alert-settings-section">
              <h3>Notification</h3>

              <label className="toggle-label master-toggle">
                <input
                  type="checkbox"
                  checked={!!config.enabled}
                  onChange={(e) => setConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                />
                <span><b>Enable Alerts</b></span>
              </label>
              {!config.enabled && (
                <p className="subtle-note">Rules stay saved but no alerts will fire.</p>
              )}

              <div className={`settings-body ${!config.enabled ? 'is-disabled' : ''}`}>
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={!!config.soundEnabled}
                    onChange={(e) => setConfig(prev => ({ ...prev, soundEnabled: e.target.checked }))}
                  />
                  Play Sound
                </label>

                {config.soundEnabled && (
                  <div className="sound-selector" role="radiogroup" aria-label="Alert sound">
                    {SOUND_PRESETS.map(preset => (
                      <label
                        key={preset.id}
                        className={`sound-choice ${config.soundId === preset.id ? 'is-selected' : ''}`}
                      >
                        <input
                          type="radio"
                          name="alert-sound"
                          value={preset.id}
                          checked={config.soundId === preset.id}
                          onChange={() => setConfig(prev => ({ ...prev, soundId: preset.id }))}
                        />
                        <span className="sound-choice-label">
                          <b>{preset.label}</b>
                          <span className="sound-choice-desc">{preset.description}</span>
                        </span>
                        <button
                          type="button"
                          className="sound-preview-btn"
                          onClick={(e) => { e.preventDefault(); playAlertSound(preset.id); }}
                          aria-label={`Preview ${preset.label}`}
                          title="Preview"
                        >▶</button>
                      </label>
                    ))}
                  </div>
                )}

                <label className="toggle-label" style={{ marginTop: '6px' }}>
                  <input
                    type="checkbox"
                    checked={!!config.toastEnabled}
                    onChange={(e) => setConfig(prev => ({ ...prev, toastEnabled: e.target.checked }))}
                  />
                  Show Toast
                </label>

                {config.toastEnabled && (
                  <div className="permission-status">
                    <span className={`perm-dot perm-${permissionStatus}`} />
                    <span className="perm-text">
                      Browser permission: <b>{permissionLabel}</b>
                    </span>
                    {permissionStatus !== 'granted' && permissionStatus !== 'unsupported' && (
                      <button
                        type="button"
                        className="link-btn"
                        onClick={handleRequestPermission}
                      >
                        Request
                      </button>
                    )}
                  </div>
                )}
                {config.toastEnabled && isEmbeddedInFrame() && permissionStatus !== 'granted' && permissionStatus !== 'unsupported' && !permissionError && (
                  <p className="permission-embed-brief" role="note">
                    <b>Embedded:</b> Request opens a small <b>Fusion</b> window on top. Approve there — you
                    stay on the main page; the embed updates automatically.
                  </p>
                )}

                {config.toastEnabled && permissionError === 'top-denied' && (
                  <p className="permission-denied-hint" role="status">
                    The browser is blocking notifications for <b>this site (Fusion)</b>. Open the site menu
                    (lock) next to the address bar so the URL is <b>fusion.opcioguru.com</b> → Site settings
                    → Notifications → Allow → Request again.
                  </p>
                )}

                {config.toastEnabled && permissionError === 'default-pending' && (
                  <p className="permission-denied-hint" role="status">
                    No permission yet. If you closed the browser prompt, reset it in site settings for this URL.
                  </p>
                )}

                {config.toastEnabled && permissionError === 'popup-blocked' && (
                  <p className="permission-denied-hint" role="status">
                    The browser blocked the helper window. Allow popups for <code>fusion.opcioguru.com</code> (or
                    this site) in the address bar, then click <b>Request</b> again.
                  </p>
                )}

                {config.toastEnabled && permissionError && typeof permissionError === 'string' && !['top-denied', 'default-pending', 'popup-blocked'].includes(permissionError) && (
                  <p className="permission-denied-hint" role="status">{permissionError}</p>
                )}

                <div className="settings-footer">
                  <button className="test-alert-btn" onClick={handleTestAlert}>Test Alerts</button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AlertsManager;
